import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fileUrl } from "@/components/ImageUpload";
import { buildWinnerCard } from "@/lib/winnerCard";

const PLACE = { 1: "Champion", 2: "Reserve" };

const shareWinner = async (w, division) => {
  const url = `${window.location.origin}/mustangs/${w.horse_id}`;
  const text = `${w.horse_name} is the ${division} ${PLACE[w.placing] || "winner"} at the Bluegrass Mustang Makeover!`;
  const blob = await buildWinnerCard(w, division);
  const file = new File([blob], `${w.horse_name.replace(/[^a-z0-9]+/gi, "-")}-winner.png`, { type: "image/png" });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "Bluegrass Mustang Makeover", text: `${text} ${url}` });
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Sharing didn't work. Try again.");
    }
    return;
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  await navigator.clipboard?.writeText(`${text} ${url}`).catch(() => {});
  toast.success("Winner card downloaded and caption copied. Post it on your favorite social app!");
};

const ShareButton = ({ w, division }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await shareWinner(w, division); } finally { setBusy(false); } };
  return (
    <button data-testid={`winner-share-${w.hip_number}`} onClick={go} disabled={busy} aria-label={`Share ${w.horse_name}`}
      className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary text-white transition-transform active:scale-90 disabled:opacity-60">
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
    </button>
  );
};

const WinnerRow = ({ w, division }) => (
  <div className="flex items-center gap-2">
  <Link to={`/mustangs/${w.horse_id}`} data-testid={`winner-${w.hip_number}`}
    className="flex min-w-0 flex-1 items-center gap-3 rounded-xl bg-white/10 p-2 transition-colors hover:bg-white/20">
    <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-white/15">
      {w.photo ? <img src={fileUrl(w.photo)} alt={w.horse_name} className="h-full w-full object-cover" /> : <Trophy size={18} className="text-secondary" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-wider text-secondary">{w.placing === 1 ? "1st" : "2nd"}{w.tied ? " (tie)" : ""} · {PLACE[w.placing]}</p>
      <p className="truncate font-display text-base font-bold leading-tight">{w.horse_name}</p>
      <p className="truncate text-xs text-white/70">HIP #{w.hip_number} · {w.trainer_name}</p>
    </div>
    <span className="font-display text-lg font-bold tabular-nums">{w.overall_total}</span>
  </Link>
  <ShareButton w={w} division={division} />
  </div>
);

export const WinnersBanner = () => {
  const [divs, setDivs] = useState([]);
  useEffect(() => {
    api.get("/results/winners").then((r) => setDivs(r.data.divisions || [])).catch(() => {});
  }, []);
  if (!divs.length) return null;
  return (
    <div className="px-4 pt-6" data-testid="winners-banner">
      <div className="animate-fade-up rounded-2xl bg-primary p-4 text-white shadow-md">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-secondary"><Trophy size={15} /> Final Results</p>
        <h2 className="mt-1 font-display text-2xl font-extrabold">Division Winners</h2>
        <div className="mt-3 space-y-4">
          {divs.map((d) => (
            <div key={d.division} data-testid={`winners-${(d.division || "").toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="mb-1.5 text-sm font-bold text-white/90">{d.division}</p>
              <div className="space-y-1.5">{d.winners.map((w) => <WinnerRow key={w.horse_id} w={w} division={d.division} />)}</div>
            </div>
          ))}
        </div>
        <Link to="/scores" data-testid="winners-all-results" className="mt-4 inline-block text-sm font-bold text-secondary underline-offset-2 hover:underline">See all results →</Link>
      </div>
    </div>
  );
};
